import {
  useLazyQuery,
  useMutation,
  useQuery,
  useReactiveVar,
} from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { cutToast } from 'utility/toast';
import { TripPatch, TripUpdateInput } from 'common/types/globalTypes';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { TravelerAuthorizationCommonData } from 'travelAuthorization/TravelPlan/view/FormView/__generated__/TravelerAuthorizationCommonData';
import { TripBreakdownSearch_tripBreakdownSearch_nodes } from 'travelAuthorization/TripBreakdown/list/__generated__/TripBreakdownSearch';
import { setUserDefaults, travelerDialogVisibleVar } from 'utility/cache/ui';
import { SectionProps } from '../..';
import { setTripsModalState } from '../../..';
import TRIP_BREAKDOWN from '../../../../../../../TripBreakdown/list/TripBreakdown.graphql';
import TRAVEL_AUTHORIZATION from '../../../../../TravelAuthorization.graphql';
import COMMON_DATA from '../../../../TravelerAuthorizationCommonData.graphql';
import { Trip, TripVariables } from '../../__generated__/Trip';
import {
  TripUpdate,
  TripUpdateVariables,
} from '../../__generated__/TripUpdate';
import { FormView } from '../../FormView';
import TRIP from '../../Trip.graphql';
import TRIP_UPDATE from '../../TripUpdate.graphql';
import { TripCreateFormValues } from '../../types';
import {
  getDefaultValues,
  getDeletedTripItems,
  getNewTripDistributions,
  getNewTripItems,
  getRemovedTripDistributions,
  getSections,
  getTripPatch,
  getUpdatedTripDistributions,
  getUpdateTripItems,
  sortMapDescendingBySortOrder,
} from '../../utils';
import { validationSchema } from '../../validations';

import {
  TripBreakdown,
  TripBreakdownVariables,
} from 'travelAuthorization/TripBreakdown/list/__generated__/TripBreakdown';

interface TripBreakDownDetailsModalProps {
  selectedItem: TripBreakdownSearch_tripBreakdownSearch_nodes;
  onClose: () => void;
}
export const TripBreakDownDetailsModal: React.FC<
  TripBreakDownDetailsModalProps
> = ({ selectedItem, onClose }) => {
  const formMethods = useForm<TripCreateFormValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });
  const travelerDialogVisibleDefault = useReactiveVar(travelerDialogVisibleVar);

  const { reset, handleSubmit, trigger } = { ...formMethods };
  const userDefaults = useReactiveVar(setUserDefaults);
  const [updateTrip, { loading: updateTripLoading }] = useMutation<
    TripUpdate,
    TripUpdateVariables
  >(TRIP_UPDATE, {
    errorPolicy: 'all',
  });
  const [getTrip] = useLazyQuery<TripBreakdown, TripBreakdownVariables>(
    TRIP_BREAKDOWN,
    { fetchPolicy: 'network-only' }
  );
  const onHandleSubmit = async (
    values: TripCreateFormValues,
    closeAfterComplete: boolean
  ) => {
    {
      const defaultValues = getDefaultValues(
        tripData,
        commonData,
        userDefaults
      );
      const { trip, tripItems } = { ...defaultValues } as TripCreateFormValues;
      const tripPatch: TripPatch = getTripPatch(trip, values.trip);

      const newTripItems = getNewTripItems(values.tripItems);
      const updatedTripItems = getUpdateTripItems(tripItems, values.tripItems);
      const { deletedTripItems, deletedData } = getDeletedTripItems(
        tripItems,
        values.tripItems
      );

      const tripDistributionCreate = getNewTripDistributions(
        values,
        userDefaults
      );
      const tripDistributionDelete = getRemovedTripDistributions(
        tripData,
        values,
        deletedData
      );

      const tripDistributionUpdate = getUpdatedTripDistributions(
        values,
        defaultValues,
        deletedTripItems
      );

      const { errors, data } = await updateTrip({
        variables: {
          input: {
            tripId: !!travelerDialogVisibleDefault
              ? tripData?.trip?.id!
              : tripsModalState?.trip?.id!,
            rowTimestamp: tripData?.trip?._rowTimestamp!,
            tripPatch: tripPatch,
            tripItemCreate: newTripItems,
            tripItemUpdate: updatedTripItems ? updatedTripItems : undefined,
            tripItemDelete: deletedTripItems,
            tripDistributionDelete,
            tripDistributionCreate,
            tripDistributionUpdate,
          } as TripUpdateInput,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: TRAVEL_AUTHORIZATION,
            variables: {
              id: selectedItem.travelAuthorization?.id,
            },
          },
        ],
      });

      if (errors?.length) {
        cutToast.error(errors[0].message);
      } else {
        cutToast.success('Trip item updated successfully');
        getTrip({ variables: { id: selectedItem.id } });
        if (closeAfterComplete) onClose();
      }
    }
  };
  const {
    data: tripData,
    loading: loadingTrip,
    refetch,
  } = useQuery<Trip, TripVariables>(TRIP, {
    variables: { id: selectedItem.id! },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });
  const tripsModalState = useReactiveVar(setTripsModalState);
  const { data: commonData } = useQuery<TravelerAuthorizationCommonData>(
    COMMON_DATA,
    { nextFetchPolicy: 'cache-only' }
  );
  const [tripItemsState, setTripItemsState] = useState<
    Map<string, SectionProps>
  >(new Map());
  const [layoutAdded, setLayoutAdded] = useState<string | null>(null);
  const setDefault = useCallback(() => {
    if (tripData) {
      const defaultValues = getDefaultValues(
        tripData,
        commonData,
        userDefaults
      );
      const { map, initialLayout } = getSections(
        tripData,
        commonData,
        layoutAdded
      );

      if (!tripsModalState.isNew || !!travelerDialogVisibleDefault) {
        setLayoutAdded(initialLayout);
        const sortedMap = sortMapDescendingBySortOrder(map);
        if (sortedMap) setTripItemsState(sortedMap);
      }

      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonData, reset, tripData]);

  useEffect(() => {
    setDefault();
  }, [setDefault, trigger, tripData]);

  return (
    <FormProvider {...formMethods}>
      <FormView
        isTripDetailModal
        isOpen
        refetch={refetch}
        onSave={(closeAfterComplete) => {
          handleSubmit((values) => {
            onHandleSubmit(values, closeAfterComplete);
          })();
        }}
        tripItemsState={tripItemsState}
        onTripItemsUpdate={(mapData, adding) => {
          if (adding) {
            setTripItemsState(() => {
              const newMap = new Map(Array.from(mapData));
              return newMap;
            });
          }
        }}
        businessUnitId={selectedItem.businessUnitId}
        inputsDisabled={!selectedItem.travelAuthorization?._isUpdatable}
        travelAuthorizationData={selectedItem.travelAuthorization}
        layoutAdded={layoutAdded}
        commonData={commonData}
        tripData={tripData?.trip}
        loading={false}
        createTripLoading={false}
        updateTripLoading={false}
        loadingTrip={loadingTrip}
        onClose={onClose}
      />
    </FormProvider>
  );
};
